import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import BaseModal from '../common/BaseModal';

// Predefiniowane powody zwrotu
const RETURN_REASONS = [
  { value: '', label: '-- Wybierz powód zwrotu --' },
  { value: 'wada_produktu', label: 'Wada produktu' },
  { value: 'pomylka_klienta', label: 'Pomyłka klienta' },
  { value: 'pomylka_sprzedawcy', label: 'Pomyłka sprzedawcy' },
  { value: 'zmiana_decyzji', label: 'Zmiana decyzji' },
  { value: 'niezgodnosc_z_opisem', label: 'Niezgodność z opisem' },
  { value: 'uszkodzenie_opakowania', label: 'Uszkodzenie opakowania' },
  { value: 'termin_waznosci', label: 'Krótki termin ważności' },
  { value: 'duplikat', label: 'Duplikat zakupu' },
  { value: 'inne', label: 'Inny powód' }
];

const ReturnModal = ({ isOpen, onClose, transaction, onReturnComplete }) => {
  const { user: authUser } = useAuth();
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('gotowka');
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [returnData, setReturnData] = useState(null); // Dane zwrotu do druku

  // Załaduj pozycje transakcji przy otwarciu
  useEffect(() => {
    if (isOpen && transaction?.id) {
      loadTransactionItems();
      setReturnData(null);
      setSuccess('');
    }
  }, [isOpen, transaction]);

  const loadTransactionItems = async () => {
    setLoadingItems(true);
    setError('');

    try {
      const response = await api.get(`/pos/transaction/${transaction.id}/items`);

      if (response.data.success) {
        const itemsData = response.data.data.items || [];
        setItems(itemsData);

        // Inicjalizuj zaznaczenie - domyślnie wszystkie pozycje odznaczone
        const initial = {};
        itemsData.forEach(item => {
          initial[item.id] = {
            selected: false,
            quantity: item.ilosc_do_zwrotu || 0,
            maxQuantity: item.ilosc_do_zwrotu || 0,
            reason: ''
          };
        });
        setSelectedItems(initial);
      } else {
        setError(response.data.message || 'Błąd pobierania pozycji');
      }
    } catch (err) {
      console.error('Błąd:', err);
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoadingItems(false);
    }
  };

  const toggleItem = (itemId) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        selected: !prev[itemId].selected
      }
    }));
  };

  const updateQuantity = (itemId, value) => {
    const qty = parseFloat(value) || 0;
    const maxQty = selectedItems[itemId]?.maxQuantity || 0;

    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: Math.min(Math.max(0, qty), maxQty)
      }
    }));
  };

  const updateItemReason = (itemId, value) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        reason: value
      }
    }));
  };

  const selectAll = () => {
    setSelectedItems(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (updated[key].maxQuantity > 0) {
          updated[key].selected = true;
        }
      });
      return updated;
    });
  };

  const deselectAll = () => {
    setSelectedItems(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        updated[key].selected = false;
      });
      return updated;
    });
  };

  const calculateTotal = () => {
    let total = 0;
    items.forEach(item => {
      const sel = selectedItems[item.id];
      if (sel?.selected && sel.quantity > 0) {
        total += item.cena_jednostkowa * sel.quantity;
      }
    });
    return total.toFixed(2);
  };

  const getSelectedCount = () => {
    return Object.values(selectedItems).filter(s => s.selected && s.quantity > 0).length;
  };

  // Pobierz pełny tekst powodu
  const getFullReason = () => {
    if (reason === 'inne') {
      return customReason || 'Inny powód';
    }
    const found = RETURN_REASONS.find(r => r.value === reason);
    return found ? found.label : reason;
  };

  const handleSubmit = async () => {
    // Walidacja powodu
    if (!reason) {
      setError('Wybierz powód zwrotu');
      return;
    }

    if (reason === 'inne' && !customReason.trim()) {
      setError('Opisz powód zwrotu');
      return;
    }

    // Walidacja pozycji
    const itemsToReturn = [];

    items.forEach(item => {
      const sel = selectedItems[item.id];
      if (sel?.selected && sel.quantity > 0) {
        itemsToReturn.push({
          pozycja_id: item.id,
          ilosc_zwracana: sel.quantity,
          powod: sel.reason || getFullReason()
        });
      }
    });

    if (itemsToReturn.length === 0) {
      setError('Wybierz przynajmniej jedną pozycję do zwrotu');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/pos/returns', {
        transaction_id: transaction.id,
        items: itemsToReturn,
        payment_method: paymentMethod,
        reason: getFullReason(),
        cashier: authUser?.login || 'kasjer',
        location_id: transaction.location_id
      });

      if (response.data.success) {
        // Przygotuj dane do druku
        const returnedItems = items.filter(item => {
          const sel = selectedItems[item.id];
          return sel?.selected && sel.quantity > 0;
        }).map(item => {
          const sel = selectedItems[item.id];
          return {
            ...item,
            ilosc_zwracana: sel.quantity,
            wartosc_zwrotu: (item.cena_jednostkowa * sel.quantity).toFixed(2)
          };
        });

        setReturnData({
          ...response.data.data,
          items: returnedItems,
          reason: getFullReason(),
          payment_method: paymentMethod,
          transaction: transaction,
          cashier: authUser?.login || 'kasjer'
        });

        setSuccess(`Zwrot ${response.data.data.return_number} został utworzony. ` +
                   `Kwota: ${response.data.data.total_brutto} zł`);

        // Nie zamykaj automatycznie - pozwól użytkownikowi wydrukować
      } else {
        setError(response.data.message || 'Błąd tworzenia zwrotu');
      }
    } catch (err) {
      console.error('Błąd:', err);
      setError(err.response?.data?.message || 'Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  // Funkcja drukowania dokumentu zwrotu - otwiera stronę z PDF korekty paragonu
  const handlePrintReturn = () => {
    if (!returnData) return;

    // Otwórz stronę z PDF korekty paragonu używając ID zwrotu
    const returnId = returnData.return_id || returnData.id;
    if (returnId) {
      window.open(`/pos/correction-print/${returnId}`, '_blank');
    } else {
      toast.error('Błąd: Brak ID zwrotu do wydruku');
    }
  };

  // Zamknij po zakończeniu
  const handleClose = () => {
    if (returnData) {
      onReturnComplete && onReturnComplete(returnData);
    }
    onClose();
  };

  const footer = returnData ? (
    // Po sukcesie - pokaż przyciski druku i zamknij
    <>
      <button
        type="button"
        className="btn btn-success btn-sm"
        onClick={handlePrintReturn}
        style={{ fontSize: '11px' }}
      >
        <i className="fas fa-print me-1"></i>
        Drukuj dokument zwrotu
      </button>
      <button
        type="button"
        className="btn btn-primary btn-sm"
        onClick={handleClose}
        style={{ fontSize: '11px' }}
      >
        <i className="fas fa-check me-1"></i>
        Zamknij
      </button>
    </>
  ) : (
    // Przed zwrotem - pokaż przyciski anuluj i wykonaj
    <>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={handleClose}
        disabled={loading}
        style={{ fontSize: '11px' }}
      >
        <i className="fas fa-times me-1"></i>
        Anuluj
      </button>
      <button
        type="button"
        className="btn btn-danger btn-sm"
        onClick={handleSubmit}
        disabled={loading || getSelectedCount() === 0 || !reason}
        style={{ fontSize: '11px' }}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-1"></span>
            Przetwarzanie...
          </>
        ) : (
          <>
            <i className="fas fa-undo me-1"></i>
            Wykonaj zwrot ({calculateTotal()} zł)
          </>
        )}
      </button>
    </>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <>
          <i className="fas fa-undo me-2"></i>
          Zwrot do paragonu {transaction?.receipt_number || transaction?.numer_paragonu || `#${transaction?.id}`}
        </>
      }
      headerStyle={{ backgroundColor: 'var(--danger)' }}
      size="lg"
      footer={footer}
      bodyStyle={{ fontSize: '11px' }}
    >
      {error && (
        <div className="alert alert-danger alert-dismissible fade show py-1 mb-2" style={{ fontSize: '11px' }}>
          <i className="fas fa-exclamation-circle me-1"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success py-1 mb-2" style={{ fontSize: '11px' }}>
          <i className="fas fa-check-circle me-1"></i>
          {success}
        </div>
      )}

      {/* Informacje o transakcji */}
      <div className="card mb-2">
        <div className="card-body py-1 px-2">
          <div className="row">
            <div className="col-md-4">
              <small className="text-muted" style={{ fontSize: '10px' }}>Data:</small>
              <div style={{ fontSize: '11px' }}><strong>{transaction?.data_transakcji || transaction?.date}</strong></div>
            </div>
            <div className="col-md-4">
              <small className="text-muted" style={{ fontSize: '10px' }}>Wartość paragonu:</small>
              <div style={{ fontSize: '11px' }}><strong>{parseFloat(transaction?.total_amount || transaction?.suma_brutto || transaction?.total || 0).toFixed(2)} zł</strong></div>
            </div>
            <div className="col-md-4">
              <small className="text-muted" style={{ fontSize: '10px' }}>Płatność:</small>
              <div style={{ fontSize: '11px' }}><strong>{transaction?.forma_platnosci || transaction?.payment_method}</strong></div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista pozycji */}
      <div className="mb-2">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="fw-bold" style={{ fontSize: '11px' }}>Pozycje do zwrotu:</span>
          <div>
            <button
              className="btn btn-outline-primary btn-sm me-1 py-0 px-2"
              onClick={selectAll}
              disabled={loadingItems}
              style={{ fontSize: '11px' }}
            >
              Zaznacz wszystkie
            </button>
            <button
              className="btn btn-outline-secondary btn-sm py-0 px-2"
              onClick={deselectAll}
              disabled={loadingItems}
              style={{ fontSize: '11px' }}
            >
              Odznacz wszystkie
            </button>
          </div>
        </div>

        {loadingItems ? (
          <div className="text-center py-2">
            <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
            <p className="mt-1 mb-0 text-muted" style={{ fontSize: '10px' }}>Ładowanie pozycji...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="alert alert-warning py-1 mb-0" style={{ fontSize: '11px' }}>
            <i className="fas fa-exclamation-triangle me-1"></i>
            Brak pozycji do wyświetlenia
          </div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: '200px' }}>
            <table className="table table-sm table-hover mb-0" style={{ fontSize: '11px' }}>
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: '30px', padding: '4px' }}></th>
                  <th style={{ padding: '4px' }}>Produkt</th>
                  <th className="text-center" style={{ width: '90px', padding: '4px' }}>Cena brutto</th>
                  <th className="text-center" style={{ width: '80px', padding: '4px' }}>Ilość</th>
                  <th className="text-center" style={{ width: '90px', padding: '4px' }}>Wartość brutto</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const sel = selectedItems[item.id] || {};
                  const canReturn = item.ilosc_do_zwrotu > 0;
                  const returnValue = (item.cena_jednostkowa * (sel.quantity || 0)).toFixed(2);

                  return (
                    <tr
                      key={item.id}
                      className={sel.selected ? 'table-warning' : ''}
                      style={{ opacity: canReturn ? 1 : 0.5 }}
                    >
                      <td className="text-center" style={{ padding: '4px' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={sel.selected || false}
                          onChange={() => toggleItem(item.id)}
                          disabled={!canReturn}
                          style={{ width: '14px', height: '14px' }}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <div style={{ fontSize: '11px' }}><strong>{item.nazwa_produktu}</strong></div>
                        <small className="text-muted" style={{ fontSize: '9px' }}>
                          {item.kod_produktu && <span className="me-1">Kod: {item.kod_produktu}</span>}
                          <span>
                            Kupiono: {item.ilosc} {item.jednostka || 'szt'}
                            {item.ilosc_zwrocona > 0 && (
                              <span className="text-danger ms-1">
                                (zwrócono: {item.ilosc_zwrocona})
                              </span>
                            )}
                          </span>
                        </small>
                      </td>
                      <td className="text-center" style={{ padding: '4px', fontSize: '11px' }}>
                        {/* Gdy byl rabat: cena katalogowa przekreslona -> cena po rabacie (kwota zwrotu).
                            Kaucja nie ma katalogowej (nie podlega rabatowi) - pokazuje pelna cene. */}
                        {item.cena_jednostkowa_katalogowa != null
                          && parseFloat(item.cena_jednostkowa_katalogowa) !== parseFloat(item.cena_jednostkowa) ? (
                          <div style={{ lineHeight: 1.15 }}>
                            <span className="text-muted" style={{ textDecoration: 'line-through', fontSize: '9px' }}>
                              {parseFloat(item.cena_jednostkowa_katalogowa).toFixed(2)}
                            </span>
                            <div><strong>{parseFloat(item.cena_jednostkowa).toFixed(2)} zł</strong></div>
                            <span className="text-success" style={{ fontSize: '8px' }}>po rabacie</span>
                          </div>
                        ) : (
                          <span>{parseFloat(item.cena_jednostkowa).toFixed(2)} zł</span>
                        )}
                      </td>
                      <td className="text-center" style={{ padding: '4px' }}>
                        {canReturn ? (
                          <input
                            type="number"
                            className="form-control form-control-sm text-center"
                            value={sel.quantity || 0}
                            onChange={(e) => updateQuantity(item.id, e.target.value)}
                            min="0"
                            max={item.ilosc_do_zwrotu}
                            step="1"
                            disabled={!sel.selected}
                            style={{ width: '50px', margin: '0 auto', fontSize: '10px', padding: '2px' }}
                          />
                        ) : (
                          <span className="badge bg-secondary" style={{ fontSize: '9px' }}>0</span>
                        )}
                      </td>
                      <td className="text-center" style={{ padding: '4px', fontSize: '11px' }}>
                        {sel.selected ? (
                          <strong className="text-danger">{returnValue} zł</strong>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Metoda płatności zwrotu */}
      <div className="row mb-2">
        <div className="col-md-6">
          <label className="form-label mb-1" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Metoda zwrotu pieniędzy:</label>
          <div style={{ display: 'flex', gap: '6px', width: '100%' }}>
            {[
              { key: 'gotowka', label: 'Gotówka', icon: '💵', color: 'var(--success)' },
              { key: 'karta', label: 'Karta', icon: '💳', color: 'var(--info)' },
              { key: 'blik', label: 'BLIK', icon: '📱', color: 'var(--orange)' }
            ].map(pm => (
              <button
                key={pm.key}
                type="button"
                onClick={() => setPaymentMethod(pm.key)}
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  fontSize: '11px',
                  fontWeight: paymentMethod === pm.key ? '700' : '400',
                  cursor: 'pointer',
                  border: paymentMethod === pm.key ? `2px solid ${pm.color}` : '1px solid var(--border)',
                  backgroundColor: paymentMethod === pm.key ? pm.color : 'var(--card-bg)',
                  color: paymentMethod === pm.key ? '#fff' : 'var(--text-secondary)',
                  transition: 'all 0.15s ease'
                }}
              >
                <span style={{ marginRight: '4px' }}>{pm.icon}</span>
                {pm.label}
              </button>
            ))}
          </div>
        </div>
        <div className="col-md-6">
          <label className="form-label mb-1" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            Powód zwrotu: <span className="text-danger">*</span>
          </label>
          <select
            className="form-select form-select-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ fontSize: '11px' }}
          >
            {RETURN_REASONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          {reason === 'inne' && (
            <input
              type="text"
              className="form-control form-control-sm mt-1"
              placeholder="Opisz powód zwrotu..."
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              style={{ fontSize: '11px' }}
            />
          )}
        </div>
      </div>

      {/* Podsumowanie */}
      <div className="card bg-light">
        <div className="card-body py-2 px-3">
          <div className="row align-items-center">
            <div className="col">
              <span className="text-muted" style={{ fontSize: '10px' }}>Pozycje do zwrotu:</span>
              <strong className="ms-1" style={{ fontSize: '12px' }}>{getSelectedCount()}</strong>
            </div>
            <div className="col text-end">
              <span className="text-muted" style={{ fontSize: '10px' }}>Kwota zwrotu (brutto):</span>
              <span className="ms-1 fw-bold text-danger" style={{ fontSize: '16px' }}>{calculateTotal()} zł</span>
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default React.memo(ReturnModal);
